import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { agentInvitationService, getAgencyProfile, userService } from "../../services/api";
import { toIso } from "../../utils/countryFlag";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
  Modal,
  Sheet,
  Button,
  Banner,
  AndOrDivider,
  RadioDot,
  FieldLabel,
  FieldShell,
  FieldInput,
  FieldError,
  PhoneField,
  MailIcon,
  CheckIcon,
  AlertCircleIcon,
} from "./v2";

// Invite agent — My Agency V2 rebuild (ARTBOARD-SPEC boards 3a form +
// validation states v3, 3b country selector, 3c mobile sheet, 1h sent
// confirmation). Field set, in order:
//   First name*, Last name*, Role (Agent / Agency admin), Phone* AND
//   Email* (BOTH required, see T051 below). NO image upload — the invitee
//   sets their own photo at onboarding.
// Presentation only: server-error mapping and the submit wiring
// (agentInvitationService.sendAgentInvitation → POST
// /users/agent-invites/) are carried over 1:1 from the previous build
// (FUNCTION-INVENTORY §2.7). New vs. old presentation: success shows the
// board-1h in-modal confirmation ("Invitation sent" + "Invite another")
// instead of a toast; the invite still lands in the Pending invitations
// panel via onSuccess(). The old reset quirk (role reset to invalid
// lowercase "agent", country never cleared) is deliberately fixed
// (FUNCTION-TODO known-dead list #5).
//
// Validation timing (FOLLOWUP-PACK B1 / FUNCTION-TODO I-4, APPROVED
// change, board 3a validation v3): field errors appear on BLUR, the Send
// button is disabled (#A8C8F0 via the v2 Button disabled variant) while
// the form is invalid and enabled once valid. The RULES are the existing
// ones (names required, contact channels, email format) plus I-2:
// client-side per-country phone validation via libphonenumber-js
// ("Not a valid number for {country} (+{dial})."). The server's E.164
// normalization/validation stays authoritative; server errors still
// surface inline/banner at submit time. Payload and handlers unchanged.
// A bare dial code with no national digits counts as "no phone entered"
// (a dial code alone is not a reachable channel).
//
// T051 — BOTH channels are required, phone AND email.
//   Marin, 2026-07-29: "Fix fuer ticket 51 Phone and E-Mail das ist ok so
//   das beides rein muss". This deliberately supersedes the artboard's
//   "Phone AND/OR Email (at least one channel required)"
//   (ARTBOARD-SPEC.md:520/:23) for this surface.
//   Root cause it closes: email is the ONLY channel an invite is
//   dispatched on and the ONLY key the accept endpoint matches the
//   invitee by, so a phone-only invite was accepted with a 200 and the
//   confirmation promised delivery — while nothing was ever sent and the
//   invite could never be accepted. The phone stays mandatory so the
//   planned SMS lane (ARTBOARD-SPEC.md:763, "phone-only invite → SMS
//   confirmation state") has its data from day one. Building that SMS
//   channel is deferred feature work, NOT part of this fix.
//   Consequences here: the single `contact` ("at least one") error is
//   replaced by per-field required errors, the contact box no longer
//   advertises "at least one channel"/"AND / OR", and the sent
//   confirmation names the email it was sent to.
//
// I-1 (follow-up wave 2, boards 3a/3b): the phone country defaults to the
// agency's HQ country when one is resolvable (see the defaultCountry block
// in the component); the user's own country choice is never overridden.
//
// T052 — the phone input no longer alters what was typed, and the
// fallback is no longer France:
//   • PhoneField now runs with enableLongNumbers + an editable country
//     code, so digits past the selected country's mask are KEPT (the
//     I-2 per-country message then flags them) and a pasted foreign
//     number is resolved instead of discarded. Before this, a 10-digit
//     number typed into the France-defaulted field was truncated to 9
//     and submitted silently as a valid-looking +33 landline.
//   • the default country is RESOLVED, not assumed: agency HQ country →
//     the logged-in admin's own phone number's country → neutral (no
//     country preselected, the admin types the full international
//     number). 'fr' was arbitrary — every agency with a null HQ country
//     was being pushed into France's number format.

// Values match the backend role_type enum ("Agent" | "AgencyAdmin", a89158b).
const ROLE_OPTIONS = [
  { value: "Agent", title: "Agent", desc: "Manages their own players and requests." },
  { value: "AgencyAdmin", title: "Agency admin", desc: "Everything an agent can, plus members and settings." },
];

const EMAIL_RE = /\S+@\S+\.\S+/;

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  role: "Agent",
  phone: "",
  country: "", // ISO2 from the phone country-selector; server normalizes E.164
  email: "",
};

// Sub-label inside the Contact box: 12px/500 #64748B (spec CMP-INPUT).
// T051: both channels are mandatory, so both sub-labels carry the same
// required asterisk the FieldLabel component uses for First/Last name.
const SubLabel = ({ children }) => (
  <span className="text-xs font-medium text-[#64748B]">
    {children}
    <span style={{ color: "#DC2626" }}> *</span>
  </span>
);

// Per-country phone check (FUNCTION-TODO I-2, board 3a validation v3).
// `phone` is the react-phone-input-2 value: dial code + national digits,
// unformatted. `meta` is the library's country data captured on change
// ({ name, dialCode }). Returns { hasPhone, error }:
//   hasPhone — national digits present beyond the dial code (a bare dial
//              code is not a contact channel);
//   error    — the artboard message when the number is not valid for the
//              selected country (libphonenumber-js; server E.164 stays
//              authoritative).
const checkPhone = (phone, meta) => {
  const digits = (phone || "").replace(/\D/g, "");
  const hasPhone = digits.length > (meta.dialCode || "").length;
  if (!hasPhone) return { hasPhone: false, error: "" };
  const valid = isValidPhoneNumber(`+${digits}`);
  return {
    hasPhone: true,
    error: valid
      ? ""
      : // i18n template: "Not a valid number for {country} (+{dial})."
        `Not a valid number for ${meta.name} (+${meta.dialCode}).`,
  };
};

// Pure form validation — the existing submit-time rules (names required,
// contact channels, email format) + the I-2 phone check. Used both to
// derive the disabled state of Send (B1) and to show errors on blur.
// T051: phone and email are BOTH required, each reported on its own
// field (the old single "at least one channel" message is gone).
const computeErrors = (data, phoneMeta) => {
  const e = {};
  if (!data.first_name.trim()) e.first_name = "Required.";
  if (!data.last_name.trim()) e.last_name = "Required.";
  const hasEmail = data.email.trim().length > 0;
  const phone = checkPhone(data.phone, phoneMeta);
  if (!phone.hasPhone) e.phone = "Add a phone number.";
  else if (phone.error) e.phone = phone.error;
  if (!hasEmail) e.email = "Add an email address.";
  else if (!EMAIL_RE.test(data.email)) e.email = "Enter a valid email address.";
  return e;
};

// T052 — country helpers for the resolved default (no hardcoded country).
// `getCountries()` is the picker's own country list, so an ISO2 that
// survives this guard is always selectable.
const normaliseIso = (iso) => {
  const up = (iso || "").toUpperCase();
  return up && getCountries().includes(up) ? up : null;
};

// ISO2 of a stored phone number (E.164 or bare digits), or null.
const countryOfPhone = (raw) => {
  const s = (raw || "").trim();
  if (!s) return null;
  const e164 = s.startsWith("+") ? s : `+${s.replace(/\D/g, "")}`;
  try {
    return parsePhoneNumberFromString(e164)?.country || null;
  } catch (err) {
    return null;
  }
};

// { name, dialCode } for the resolved default country, in the shape the
// library hands to onChange — so the I-2 message and the "bare dial code
// is not a channel" rule are correct BEFORE the first change event too
// (react-phone-input-2 prefills the dial code without firing onChange).
// English names match the library's own `data.name`, which is what the
// message template was written against. No country → neutral meta.
const metaForCountry = (iso) => {
  const up = (iso || "").toUpperCase();
  if (!up) return { name: "", dialCode: "" };
  try {
    return {
      name: new Intl.DisplayNames(["en"], { type: "region" }).of(up) || up,
      dialCode: getCountryCallingCode(up),
    };
  } catch (err) {
    return { name: up, dialCode: "" };
  }
};

const AgentInvitationModal = ({ isOpen, onClose, onSuccess }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  // Selected phone country ({ name, dialCode }) for the I-2 message —
  // null until the user touches the field, then the library's own data.
  const [phoneMeta, setPhoneMeta] = useState(null);
  // Board 1h: the invited EMAIL, shown bold in the sent confirmation —
  // non-null renders the sent screen.
  const [sentTo, setSentTo] = useState(null);

  // I-1 — smart default: the phone selector defaults to the agency's HQ
  // country (boards 3a/3b, BINDING footnote). Source: GET
  // /users/agency/profile/ → data.country — a FREE-TEXT country name from
  // onboarding (NOT guaranteed ISO2, may be null). Mapped with the
  // platform's existing name→ISO2 resolver (utils/countryFlag toIso) and
  // guarded via libphonenumber's country list so the picker never
  // receives a code it can't select.
  //
  // T052 — the null-HQ case no longer falls back to France. Chain:
  //   1. agency HQ country (above);
  //   2. the country of the logged-in admin's OWN phone number
  //      (GET /users/profile/ → mobile) — a fact about this user, not a
  //      guess about the invitee;
  //   3. neutral — "" leaves the picker with no country preselected and
  //      the admin types the full international number (the library
  //      resolves the country from the dial code they type or paste).
  // Resolved once on first open and never awaited by the UI — the
  // default applies when the data arrives, and ONLY while the user
  // hasn't touched the phone field: react-phone-input-2 resets the input
  // to the bare dial code on a `country` prop change (without firing
  // onChange), so a late override of a user's choice would also desync
  // the typed value.
  const [defaultCountry, setDefaultCountry] = useState(""); // lowercase ISO2 | ""
  const defaultRequestedRef = useRef(false);
  const phoneTouchedRef = useRef(false);

  useEffect(() => {
    if (!isOpen || defaultRequestedRef.current) return undefined;
    defaultRequestedRef.current = true;
    let cancelled = false;
    (async () => {
      let iso = null;
      try {
        const res = await getAgencyProfile();
        iso = normaliseIso(toIso(res?.data?.data?.country ?? null));
      } catch (e) {
        // No HQ source reachable → try the admin's own number.
      }
      if (!iso) {
        try {
          const res = await userService.getProfile();
          const data = res?.data ?? {};
          iso = normaliseIso(countryOfPhone(data.mobile ?? data?.data?.mobile));
        } catch (e) {
          // Neither source reachable → neutral default.
        }
      }
      if (cancelled || !iso) return; // neutral default stays
      if (phoneTouchedRef.current) return; // never override the user
      setDefaultCountry(iso.toLowerCase());
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // The country data the validation rules run against: the library's own
  // data once the user has touched the field, the resolved default until
  // then (neutral when nothing resolved).
  const effectiveMeta = phoneMeta || metaForCountry(defaultCountry);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name] || errors.submit) {
      setErrors((prev) => ({ ...prev, [name]: "", submit: "" }));
    }
  };

  // B1 blur validation: on leaving a field, surface that field's current
  // errors (and only that field's — others keep their state). Errors
  // still clear as the user types (setField), and the submit-time
  // validate() stays as the safety net.
  const handleBlur = (...fields) => () => {
    const all = computeErrors(formData, effectiveMeta);
    setErrors((prev) => {
      const next = { ...prev };
      fields.forEach((f) => {
        next[f] = all[f] || "";
      });
      return next;
    });
  };

  const validate = () => {
    const e = computeErrors(formData, effectiveMeta);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const applyServerErrors = (payload) => {
    // Accept either {errors:{field:[msg]}} or {field:[msg]} shapes.
    const src = payload?.errors || payload || {};
    const next = {};
    ["first_name", "last_name", "email", "phone"].forEach((f) => {
      if (src[f]) next[f] = Array.isArray(src[f]) ? src[f][0] : String(src[f]);
    });
    const nonField = src.non_field || src.non_field_errors || src.detail;
    if (nonField) next.submit = Array.isArray(nonField) ? nonField[0] : String(nonField);
    if (Object.keys(next).length === 0 && typeof payload === "string") next.submit = payload;
    setErrors(next);
    return next;
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setErrors({});
    // T052: the cleared field falls back to the resolved default country,
    // so the country data the rules run against must fall back with it.
    setPhoneMeta(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const response = await agentInvitationService.sendAgentInvitation({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        role_type: formData.role,
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        country: formData.country || undefined,
      });

      if (response.success) {
        // T051: the email is mandatory and is the address the invite was
        // actually sent to — the old email || phone || name fallback could
        // name a phone number that nothing was ever dispatched to.
        setSentTo(formData.email.trim());
        resetForm();
        if (onSuccess) onSuccess();
      } else {
        const applied = applyServerErrors(response.error || response.data);
        toast.error(applied.submit || "Failed to send invitation");
      }
    } catch (err) {
      console.error("Agent invitation error:", err);
      applyServerErrors(err?.response?.data);
      toast.error("Failed to send agent invitation. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      setSentTo(null);
      onClose();
    }
  };

  const handleInviteAnother = () => {
    resetForm();
    setSentTo(null);
  };

  if (!isOpen) return null;

  // Valid-email presentation state (board 3a modal 1): same regex the
  // submit validation uses — display derivation only, no new rule.
  const emailValid =
    formData.email.trim().length > 0 && EMAIL_RE.test(formData.email) && !errors.email;

  const fieldState = (name) => (errors[name] ? "error" : "default");

  // B1 — disabled-until-valid Send (board 3a modal 2, #A8C8F0 via the v2
  // Button disabled variant). Pure derivation, no state.
  const formValid =
    Object.keys(computeErrors(formData, effectiveMeta)).length === 0;
  const sendDisabled = isSubmitting || !formValid;

  // ── Board 3a/3c — form body (shared desktop/mobile, spacing per board) ──
  const nameRow = (
    <div className="flex gap-2.5" style={{ marginTop: isMobile ? 16 : 18 }}>
      <div className="flex flex-col gap-1.5 min-w-0 flex-1">
        <FieldLabel required>First name</FieldLabel>
        <FieldShell state={fieldState("first_name")} mobile={isMobile}>
          <FieldInput
            mobile={isMobile}
            value={formData.first_name}
            onChange={(e) => setField("first_name", e.target.value)}
            onBlur={handleBlur("first_name")}
            disabled={isSubmitting}
            placeholder="First name"
            className={errors.first_name ? "placeholder:text-[#C4CEDA]" : ""}
          />
        </FieldShell>
        {errors.first_name && <FieldError>{errors.first_name}</FieldError>}
      </div>
      <div className="flex flex-col gap-1.5 min-w-0 flex-1">
        <FieldLabel required>Last name</FieldLabel>
        <FieldShell state={fieldState("last_name")} mobile={isMobile}>
          <FieldInput
            mobile={isMobile}
            value={formData.last_name}
            onChange={(e) => setField("last_name", e.target.value)}
            onBlur={handleBlur("last_name")}
            disabled={isSubmitting}
            placeholder="Last name"
            className={errors.last_name ? "placeholder:text-[#C4CEDA]" : ""}
          />
        </FieldShell>
        {errors.last_name && <FieldError>{errors.last_name}</FieldError>}
      </div>
    </div>
  );

  // CMP-RADIOCARD (desktop: label + description; mobile 3c: single line).
  const roleField = (
    <div className="flex flex-col gap-1.5" style={{ marginTop: isMobile ? 13 : 14 }}>
      <FieldLabel>Role</FieldLabel>
      <div className="flex" style={{ gap: isMobile ? 8 : 10 }}>
        {ROLE_OPTIONS.map((r) => {
          const active = formData.role === r.value;
          return (
            <button
              type="button"
              key={r.value}
              onClick={() => setField("role", r.value)}
              disabled={isSubmitting}
              aria-pressed={active}
              className={[
                "flex-1 min-w-0 text-left rounded-[10px] cursor-pointer bg-transparent",
                active
                  ? "border-[1.5px] border-[#2283FF] !bg-[#F5F9FF]"
                  : "border border-[#DCE3EC] hover:border-[#B9C6D6]",
              ].join(" ")}
              style={{ padding: isMobile ? "10px 12px" : "11px 12px" }}
            >
              <span className="flex items-center gap-[7px]">
                <RadioDot selected={active} size={15} borderWidth={5} />
                <span className="text-[13.5px] font-semibold text-[#0F172A] truncate">
                  {r.title}
                </span>
              </span>
              {!isMobile && (
                <span className="block text-xs text-[#64748B] leading-[1.45] mt-[5px]">
                  {r.desc}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  const contactBox = (
    <div
      className="border border-[#E3E9F1] rounded-[11px]"
      style={{ padding: isMobile ? 13 : 14, marginTop: isMobile ? 13 : 14 }}
    >
      <div className="flex items-baseline gap-2">
        <span className="text-[12.5px] font-semibold text-[#334155]">Contact</span>
        <span className="text-[11.5px] text-[#94A3B8]">
          {/* T051: both channels required. Board 3c shortens the mobile
              string (spec note), where the sub-labels are hidden. */}
          {isMobile ? "both required" : "phone and email required"}
        </span>
      </div>
      <div className="flex flex-col gap-1.5" style={{ marginTop: 10 }}>
        {!isMobile && <SubLabel>Phone</SubLabel>}
        <PhoneField
          value={formData.phone}
          // I-1/T052: the RESOLVED default (HQ → admin's own number →
          // neutral ""). PhoneField spreads unknown props onto
          // react-phone-input-2 after its own defaults, so this wins; the
          // prop only ever changes while the field is untouched.
          country={defaultCountry}
          onChange={(value, data) => {
            // I-1: any interaction (typing or picking a country) pins the
            // user's choice — the resolved default may no longer apply.
            phoneTouchedRef.current = true;
            setFormData((prev) => ({
              ...prev,
              phone: value,
              country: (data?.countryCode || "").toUpperCase(),
            }));
            // Country data feeds the I-2 per-country message.
            if (data?.dialCode) {
              setPhoneMeta({ name: data.name, dialCode: data.dialCode });
            }
            if (errors.phone) setErrors((p) => ({ ...p, phone: "" }));
          }}
          onBlur={handleBlur("phone")}
          placeholder="Enter phone number"
          disabled={isSubmitting}
          error={Boolean(errors.phone)}
          mobile={isMobile}
        />
        {errors.phone && <FieldError>{errors.phone}</FieldError>}
      </div>
      {/* T051: the two channels are no longer alternatives — the GLY-07
          divider keeps its styling but reads "AND". */}
      <AndOrDivider label="AND" mobile={isMobile} />
      <div className="flex flex-col gap-1.5">
        {!isMobile && <SubLabel>Email</SubLabel>}
        <FieldShell
          state={errors.email ? "error" : emailValid ? "valid" : "default"}
          mobile={isMobile}
        >
          <MailIcon size={isMobile ? 15 : 14} style={{ flex: "none" }} />
          <FieldInput
            mobile={isMobile}
            type="email"
            value={formData.email}
            onChange={(e) => setField("email", e.target.value)}
            onBlur={handleBlur("email")}
            disabled={isSubmitting}
            placeholder="Email address"
          />
          {emailValid && <CheckIcon size={14} strokeWidth={2.6} style={{ flex: "none" }} />}
        </FieldShell>
        {errors.email && <FieldError>{errors.email}</FieldError>}
      </div>
    </div>
  );

  const errorBanner = errors.submit ? (
    <Banner variant="error" icon={<AlertCircleIcon />} style={{ marginTop: 14 }}>
      {errors.submit}
    </Banner>
  ) : null;

  const footnote = "Invite link expires in 7 days.";

  const sendLabel = (
    <>
      {isSubmitting && (
        <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
      )}
      {isSubmitting ? "Sending…" : "Send invitation"}
    </>
  );

  // ── Board 1h — sent confirmation (only source of this screen) ──────
  const sentBody = (
    <div className="flex flex-col items-center text-center">
      <div
        className="flex items-center justify-center rounded-full"
        style={{ width: 52, height: 52, background: "#ECFDF5" }}
      >
        <CheckIcon size={24} strokeWidth={2.2} />
      </div>
      <div className="text-base font-semibold text-[#0F172A]" style={{ marginTop: 16 }}>
        Invitation sent
      </div>
      {/* T051: name the channel the invite was actually delivered on. */}
      <div
        className="text-[13.5px] text-[#64748B]"
        style={{ marginTop: 6, lineHeight: 1.5, overflowWrap: "anywhere" }}
      >
        We sent the invitation email to{" "}
        <b className="font-semibold text-[#0F172A]">{sentTo}</b>. They will appear under
        Pending invitations until they join.
      </div>
    </div>
  );

  // ── Mobile (board 3c sheet; sent screen derived per mobile patterns) ──
  if (isMobile) {
    return (
      <Sheet
        open
        onClose={handleClose}
        title={sentTo ? undefined : "Invite an agent"}
        top={sentTo ? undefined : 64}
        closeLabel="Close"
        hideClose={Boolean(sentTo)}
      >
        {sentTo ? (
          <div style={{ padding: "16px 0 4px" }}>
            {sentBody}
            <div className="flex flex-col gap-[9px]" style={{ marginTop: 20 }}>
              <Button mobile variant="primary" onClick={handleClose}>
                Done
              </Button>
              <Button mobile variant="sheetCancel" onClick={handleInviteAnother}>
                Invite another
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            {errorBanner}
            {nameRow}
            {roleField}
            {contactBox}
            {/* Footer pinned via margin-top:auto (board 3c). */}
            <div
              className="flex flex-col gap-[9px]"
              style={{ marginTop: "auto", paddingTop: 16 }}
            >
              <span className="text-xs text-[#94A3B8] text-center">{footnote}</span>
              <Button mobile variant="primary" type="submit" disabled={sendDisabled}>
                {sendLabel}
              </Button>
            </div>
          </form>
        )}
      </Sheet>
    );
  }

  // ── Desktop — board 1h confirmation modal (400px, padding 32) ──────
  if (sentTo) {
    return (
      <Modal open onClose={handleClose} width={400} padding={32} hideClose>
        {sentBody}
        <div className="flex justify-center gap-2.5" style={{ marginTop: 20 }}>
          <Button variant="secondary" onClick={handleInviteAnother}>
            Invite another
          </Button>
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        </div>
      </Modal>
    );
  }

  // ── Desktop — board 3a form modal (460px, padding 24) ──────────────
  return (
    <Modal
      open
      onClose={handleClose}
      width={460}
      padding={24}
      title="Invite an agent"
      subtitle="Their name personalizes the invite and prefills onboarding."
      closeLabel="Close"
    >
      <form onSubmit={handleSubmit}>
        {errorBanner}
        {nameRow}
        {roleField}
        {contactBox}
        <div className="flex items-center justify-between gap-2.5" style={{ marginTop: 20 }}>
          <span className="text-xs text-[#94A3B8]">{footnote}</span>
          <div className="flex items-center gap-2.5">
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={sendDisabled}>
              {sendLabel}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AgentInvitationModal;
